import React, { useEffect, useMemo, useState, useRef } from "react";
import { Card, CardHeader, CardBody, Row, Col, Container } from "reactstrap";
import TableContainer from "../../../Components/Common/TableContainer";
import { toast } from "react-toastify";
import moment from "moment";
import { Link } from "react-router-dom";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { useNotifications } from "../../../Components/Context/NotificationContext";
import axios from "../../../utills/api";

const AdminEmergencyRequests = () => {
  document.title = "HMS | Emergency";

  const [requests, setRequests] = useState([]);
  const [bookings, setBookings] = useState([]);

  const { addNotification } = useNotifications();
  const lastNotified = useRef(0);

  const API_BASE = "emergency-request";
  const BOOKINGS_API = "bookings";

  // Optional sound (you can uncomment when ready)
  // const audioRef = useRef(new Audio("/notification.mp3"));
  // const playSound = () => {
  //   try {
  //     audioRef.current.play().catch(() => console.warn("Sound blocked until user interaction"));
  //   } catch (err) {
  //     console.error("Error playing sound:", err);
  //   }
  // };

  // Fetch emergency requests periodically
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [emergencyRes, bookingsRes] = await Promise.all([
          axios.get(API_BASE),
          axios.get(BOOKINGS_API),
        ]);

        const emergencyData = Array.isArray(emergencyRes.data) ? emergencyRes.data : [];
        const bookingData = Array.isArray(bookingsRes.data) ? bookingsRes.data : [];

        // Merge guest info from bookings
        const mergedData = emergencyData.map((req) => {
          const booking = bookingData.find((b) => b.assignRoom === req.roomNumber);
          return {
            ...req,
            guestName: booking ? booking.name : "Unknown",
            guestRoom: booking ? booking.assignRoom : req.roomNumber,
          };
        });

        setRequests(mergedData);
        setBookings(bookingData);

        // --- 🔔 Notification Logic ---
        const prevCount = lastNotified.current || 0;
        if (mergedData.length > prevCount) {
          const newCount = mergedData.length - prevCount;
          addNotification({
            title: "New Emergency Request",
            message: `${newCount} new emergency request${newCount > 1 ? "s" : ""} received.`,
            type: "info",
          });
          // playSound();
          lastNotified.current = mergedData.length;
        }
      } catch (err) {
        console.error("Error fetching emergency data:", err);
        toast.error("Failed to fetch emergency requests");
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 7000); // refresh every 7 seconds
    return () => clearInterval(interval);
  }, [addNotification]);

  // Delete handler
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this request?")) return;
    try {
      await axios.delete(`${API_BASE}/${id}`);
      setRequests((prev) => prev.filter((r) => r._id !== id));
      toast.success("Emergency request deleted");
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete request");
    }
  };

  // Capitalize helper
  const capitalize = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : "");

  // Table columns
  const columns = useMemo(
    () => [
      {
        id: "_id",
        header: "ID",
        accessorFn: (row) => row._id?.slice(0, 6) + "...",
      },
      {
        id: "guestName",
        header: "Guest Name",
        accessorFn: (row) => capitalize(row.guestName),
      },
      {
        id: "roomNumber",
        header: "Room",
        accessorFn: (row) => capitalize(row.guestRoom),
      },
      {
        id: "service",
        header: "Service",
        accessorFn: (row) => capitalize(row.service),
      },
      {
        id: "notes",
        header: "Notes",
        accessorFn: (row) => capitalize(row.notes),
      },
      {
        id: "createdAt",
        header: "Created At",
        cell: ({ row }) => (
          <div>
            <div>{moment(row.original.createdAt).format("DD-MM-YYYY")}</div>
            <div>{moment(row.original.createdAt).format("hh:mm A")}</div>
          </div>
        ),
      },
      {
        id: "action",
        header: "Action",
        cell: ({ row }) => (
          <Link
            onClick={() => handleDelete(row.original._id)}
            className="btn btn-sm btn-soft-danger"
            style={{ width: "28px", height: "28px", cursor: "pointer" }}
          >
            <i className="las la-trash-alt"></i>
          </Link>
        ),
      },
    ],
    [requests]
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Manage Emergency" pageTitle="Health" />
          <Row>
            <Col xxl={12}>
              <Card>
                <CardHeader>
                  <h5 className="mb-0">Emergency Requests</h5>
                </CardHeader>
                <CardBody>
                  <div style={{ padding: "1rem" }}>
                    <TableContainer
                      columns={columns}
                      data={requests}
                      customPageSize={5}
                      isGlobalFilter
                      isPagination
                      SearchPlaceholder="Search emergency requests..."
                      className="text-nowrap"
                    />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default AdminEmergencyRequests;
